import { type DecisionId, Dtos, pluralize } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	correctableDecisionId,
	type DataNeeded,
	type Decision,
	type ProCon,
} from "@portal/api/decisioning";
import { useDeleteDecision, useRestoreProCon } from "@portal/api/decisioning/admin";
import { Fragment, useMemo, useState } from "react";
import { ConfirmationModal } from "../../../../layout/ConfirmationModal";
import { cn } from "../../../../lib/utils";
import { Disclosure } from "../../../../patterns/disclosure";
import { RetryableLoadError } from "../../../../patterns/retryable-load-error";
import { ActionMenu, ClickableText, Tag, Text } from "../../../../primitives";
import {
	DATA_NEEDED_STATUS_LABELS,
	DECISION_STAGE_LABELS,
	DECISION_STAGE_TAG_COLORS,
	formatDecisioningDate,
} from "../../decisioningLabels";
import { useDecisioningData } from "../DecisioningDataContext";
import {
	JUMP_HIGHLIGHT_CLASSES,
	useDecisioningPanel,
	useJumpTargetProps,
} from "../DecisioningPanelContext";
import { DataNeededFulfillment } from "../data-needed/DataNeededFulfillment";
import { ItemActionMenu } from "../ItemActionMenu";
import { ProConSnapshotRow } from "../ProConSnapshotRow";
import { decisionTermParts } from "./decisionTerms";

const STAGE_DOT_CLASSES: Record<Dtos.Decisioning.DecisionStage, string> = {
	initial_submission_follow_up: "bg-info",
	post_submission_follow_up: "bg-warning",
	approve: "bg-confirm",
	decline: "bg-danger",
	close: "bg-intent-neutral-text",
};

type SnapshotDataNeeded = Pick<DataNeeded, "proConId" | "title">;
type SnapshotLinkedProCon = Pick<ProCon, "id"> & { factor: Pick<ProCon["factor"], "name"> };

const describeLinkedFactor = (
	item: SnapshotDataNeeded,
	proCons: readonly SnapshotLinkedProCon[],
): string => {
	const { title } = item;
	const target = proCons.find((proCon) => proCon.id === item.proConId);
	return target ? `${title} for ${target.factor.name}` : title;
};

const SnapshotRows = ({ decision }: { decision: Decision }) => {
	const { proCons, dataNeeded } = decision.supportingData;
	const currentById = new Map(decision.dataNeededCurrent.map((item) => [item.id, item]));
	const openItems = dataNeeded.filter((item) => item.status === "open");

	return (
		<>
			{proCons.map((proCon) => (
				<ProConSnapshotRow
					key={proCon.id}
					proCon={proCon}
					testId={`history-snapshot-factor-${proCon.id}`}
				/>
			))}
			{openItems.map((item) => {
				const current = currentById.get(item.id);
				const now = current ? DATA_NEEDED_STATUS_LABELS[current.status] : "Deleted";
				return (
					<Disclosure.Row
						key={item.id}
						trailing={
							now === DATA_NEEDED_STATUS_LABELS.open ? undefined : (
								<Text type="caption" color="tertiary">{`Now ${now.toLowerCase()}`}</Text>
							)
						}
						data-testid={`history-snapshot-request-${item.id}`}
					>
						<Text type="caption" color="warning">
							Open
						</Text>
						<div className="flex min-w-0 flex-col gap-0.5">
							<Text type="caption" color="primary" className="min-w-0">
								{describeLinkedFactor(item, proCons)}
							</Text>
							{current?.status === "received" && (
								<DataNeededFulfillment
									attachmentIds={current.fulfillmentAttachmentIds}
									answer={current.fulfillmentAnswer}
									testId={`history-snapshot-request-${item.id}-fulfillment`}
								/>
							)}
						</div>
					</Disclosure.Row>
				);
			})}
		</>
	);
};

const DecisionEntry = ({
	decision,
	superseded,
	correctable,
	isLast,
	number,
}: {
	decision: Decision;
	superseded: boolean;
	correctable: boolean;
	isLast: boolean;
	number: number;
}) => {
	const { accountId, openSheet, isExpanded, setIsExpanded } = useDecisioningPanel();
	const { userName } = useDecisioningData();
	const { deleteDecision } = useDeleteDecision(accountId);
	const jumpProps = useJumpTargetProps(decision.id);
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const testId = `history-decision-${decision.id}`;
	const termParts = decisionTermParts(decision);
	const snapshotCount =
		decision.supportingData.proCons.length +
		decision.supportingData.dataNeeded.filter((item) => item.status === "open").length;

	return (
		<li
			className={cn(
				"group/item grid list-none grid-cols-[32px_minmax(0,1fr)] gap-x-2 rounded-md",
				JUMP_HIGHLIGHT_CLASSES,
			)}
			data-testid={testId}
			data-superseded={superseded || undefined}
			{...jumpProps}
		>
			<div className="relative">
				{!isLast && (
					<span aria-hidden className="absolute -bottom-0 right-[4.5px] top-3 w-px bg-bc-default" />
				)}
				<div className="relative flex h-6 items-center justify-end gap-1.5">
					<Text
						type="caption"
						color="tertiary"
						className="tabular-nums"
						data-testid={`${testId}-number`}
					>
						{number}
					</Text>
					<span
						aria-hidden
						className={cn("size-2.5 shrink-0 rounded-full", STAGE_DOT_CLASSES[decision.stage])}
					/>
				</div>
			</div>
			<div className={cn("flex min-w-0 flex-col gap-2", isLast ? "pb-0" : "pb-5")}>
				<div className={cn("flex flex-col gap-2", superseded && "opacity-60")}>
					<div className="flex items-start gap-2">
						<div className="flex min-w-0 flex-1 flex-col items-start gap-1">
							<span className="flex min-h-6 items-center">
								<Tag color={DECISION_STAGE_TAG_COLORS[decision.stage]}>
									{DECISION_STAGE_LABELS[decision.stage]}
								</Tag>
							</span>
							<Text
								type="caption"
								color="tertiary"
								className="block tabular-nums [text-box-trim:none]"
								data-testid={`${testId}-byline`}
							>
								{`${formatDecisioningDate(decision.createdAt)} by ${userName(decision.underwriterUserId)}`}
							</Text>
						</div>
						{!superseded && (
							<ItemActionMenu itemName={`decision ${number}`} testId={`${testId}-menu`}>
								{correctable && (
									<ActionMenu.Item
										onClick={() =>
											openSheet({
												kind: "log",
												stage: decision.stage,
												supersedesDecisionId: decision.id,
											})
										}
										data-testid={`${testId}-correct`}
									>
										Correct this decision
									</ActionMenu.Item>
								)}
								<ActionMenu.Item
									tone="danger"
									onClick={() => setConfirmingDelete(true)}
									data-testid={`${testId}-delete`}
								>
									Delete
								</ActionMenu.Item>
							</ItemActionMenu>
						)}
					</div>
					{termParts.length > 0 && (
						<Text
							type="caption"
							className={cn(
								"block font-mono tabular-nums text-intent-success-text [text-box-trim:none]",
								superseded && "line-through",
							)}
							data-testid={`${testId}-terms`}
						>
							{termParts.map((part, index) => (
								<Fragment key={part}>
									<span className="whitespace-nowrap">
										{index < termParts.length - 1 ? `${part},` : part}
									</span>
									{index < termParts.length - 1 && " "}
								</Fragment>
							))}
						</Text>
					)}
					<Text
						type="body"
						color="secondary"
						className={cn(
							"block whitespace-pre-wrap break-words text-[13px] leading-[18px] [text-box-trim:none]",
							superseded && "line-through",
						)}
						data-testid={`${testId}-rationale`}
					>
						{decision.rationale}
					</Text>
				</div>
				<Disclosure
					open={isExpanded(decision.id)}
					onOpenChange={(open) => setIsExpanded(decision.id, open)}
					testId={`${testId}-snapshot`}
				>
					<Disclosure.Summary testId={`${testId}-snapshot-toggle`}>
						{`Snapshot of ${pluralize(snapshotCount, "item", "items")}`}
					</Disclosure.Summary>
					<Disclosure.Panel>
						{snapshotCount === 0 ? (
							<Disclosure.Row>
								<Text type="caption" color="tertiary">
									Nothing was on the board.
								</Text>
							</Disclosure.Row>
						) : (
							<SnapshotRows decision={decision} />
						)}
					</Disclosure.Panel>
				</Disclosure>
			</div>
			{confirmingDelete && (
				<ConfirmationModal
					closeOnEscape
					message="Delete this decision?"
					subMessage="It leaves History and is no longer used as the account's last decision. This cannot be undone."
					confirmButtonText="Delete"
					confirmButtonTestId="history-delete-confirm"
					confirmationButtonColor="danger"
					onCancel={() => setConfirmingDelete(false)}
					onConfirm={() => {
						setConfirmingDelete(false);
						deleteDecision(decision.id);
					}}
				/>
			)}
		</li>
	);
};

const RetiredFactorRow = ({ proCon }: { proCon: ProCon }) => {
	const { accountId } = useDecisioningPanel();
	const { userName } = useDecisioningData();
	const { restoreProCon } = useRestoreProCon(accountId);
	const jumpProps = useJumpTargetProps(proCon.id);
	const testId = `history-retired-${proCon.id}`;

	return (
		<li
			className={cn(
				"flex list-none items-center gap-3 rounded-md bg-surface-light px-3 py-2.5",
				JUMP_HIGHLIGHT_CLASSES,
			)}
			data-testid={testId}
			{...jumpProps}
		>
			<div className="flex min-w-0 flex-1 flex-col gap-0.5">
				<Text
					type="body"
					color="secondary"
					className="block break-words text-[13px] font-medium leading-[18px] line-through [text-box-trim:none]"
				>
					{proCon.factor.name}
				</Text>
				{proCon.retiredAt && (
					<Text
						type="caption"
						color="tertiary"
						className="block [text-box-trim:none]"
						data-testid={`${testId}-meta`}
					>
						{`Retired ${formatDecisioningDate(proCon.retiredAt)} by ${userName(proCon.retiredByUserId)}`}
					</Text>
				)}
			</div>
			<ClickableText
				type="caption"
				color="link"
				onClick={() => restoreProCon(proCon.id)}
				testId={`${testId}-restore`}
			>
				Restore
			</ClickableText>
		</li>
	);
};

export const HistoryTab = () => {
	const { decisions, retiredProCons, decisionsLoadError, retiredLoadError } = useDecisioningData();

	const { ordered, supersededIds, correctableId } = useMemo(
		() => ({
			ordered: [...decisions].sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis()),
			supersededIds: new Set<DecisionId>(
				decisions.flatMap((item) => item.supersedesDecisionId ?? []),
			),
			correctableId: correctableDecisionId(decisions),
		}),
		[decisions],
	);

	return (
		<div className="flex flex-col gap-6" data-testid="decisioning-history">
			{Option.isSome(decisionsLoadError) ? (
				<RetryableLoadError
					resource="decisions"
					onRetry={decisionsLoadError.retry}
					testId="history-load-error"
				/>
			) : ordered.length === 0 ? (
				<Text type="body" color="tertiary" data-testid="history-empty">
					No decisions logged yet.
				</Text>
			) : (
				<ol reversed role="list" className="m-0 flex list-none flex-col p-0">
					{ordered.map((decision, index) => (
						<DecisionEntry
							key={decision.id}
							decision={decision}
							superseded={supersededIds.has(decision.id)}
							correctable={decision.id === correctableId}
							isLast={index === ordered.length - 1}
							number={ordered.length - index}
						/>
					))}
				</ol>
			)}
			<section className="flex flex-col gap-2" aria-labelledby="history-retired-heading">
				<Text id="history-retired-heading" type="eyebrow" color="tertiary">
					Retired factors
				</Text>
				{Option.isSome(retiredLoadError) ? (
					<RetryableLoadError
						resource="retired factors"
						onRetry={retiredLoadError.retry}
						testId="history-retired-load-error"
					/>
				) : retiredProCons.length === 0 ? (
					<Text type="body" color="tertiary" data-testid="history-retired-empty">
						None retired. Retiring a factor moves it here; it can be restored.
					</Text>
				) : (
					<ul className="m-0 flex flex-col gap-1.5 p-0">
						{retiredProCons.map((proCon) => (
							<RetiredFactorRow key={proCon.id} proCon={proCon} />
						))}
					</ul>
				)}
			</section>
		</div>
	);
};
